import React, { useEffect } from 'react'
import { Switch, Route } from 'react-router-dom'
import './App.scss'

import { useDispatch, useSelector } from 'react-redux'
import { getFormattedDate } from '../../utilities'
import { fetchBrands } from '../../redux/brands/brandsActions'
import { fetchCalendar } from '../../redux/calendar/calendarActions'
import { fetchShips } from '../../redux/ships/shipsActions'
import { fetchSso } from '../../redux/sso/ssoActions'
import celebFavicon from '../../assets/images/favicon-x.ico'
import rclFavicon from '../../assets/images/favicon.ico'

import {
  setBrandCode,
  setCurrency,
  setShipCode,
  setDate,
} from '../../redux/selections/selectionsActions'

import {
  selectBrandCode,
  selectShipCode,
  selectDate,
} from '../../redux/selections/selectionsSelectors'
import { selectSsoError } from '../../redux/sso/ssoSelectors'
import { selectValidDates } from '../../redux/calendar/calendarSelectors'

import { selectAppContent } from '../../redux/selectors/selectAppContent'
import { selectIsValidSearch } from '../../redux/selectors/selectIsValidSearch'

import BrandForm from '../../components/BrandForm/BrandForm'

function App() {
  // App data
  const dispatch = useDispatch()
  const selectedBrandCode = useSelector(selectBrandCode)
  const content = useSelector(selectAppContent)
  const isValidSearch = useSelector(selectIsValidSearch)
  const validDates = useSelector(selectValidDates)
  const selectedShipCode = useSelector(selectShipCode)
  const selectedDate = useSelector(selectDate)
  const ssoError = useSelector(selectSsoError)

  // Effects
  useEffect(() => {
    if (ssoError) {
      window.location = 'https://secure.cruisingpower.com/SecureLogin.do'
    }
  }, [ssoError])

  useEffect(() => {
    dispatch(fetchBrands())
    dispatch(fetchShips())
    dispatch(fetchSso(new URLSearchParams(window.location.search)))
  }, [])

  useEffect(() => {
    const brands = {
      R: 'Royal',
      C: 'Celebrity',
    }
    document.title = `My ${brands[selectedBrandCode]} Cruise`

    const oldLink = document.getElementById('dynamic-fav')
    const newLink = document.createElement('link')
    newLink.id = 'dynamic-fav'
    newLink.rel = 'shortcut icon'
    newLink.href = selectedBrandCode === 'C' ? celebFavicon : rclFavicon
    document.head.removeChild(oldLink)
    document.head.appendChild(newLink)
  }, [selectedBrandCode])

  // HTML video does not replace the source file when simply replacing the source.
  // This boolean check forces re-rendering the elements
  const [shouldRender, setShouldRender] = React.useState(true)

  // get & save url search parameters
  useEffect(() => {
    setShouldRender(false)
    setTimeout(() => {
      const urlParams = new URLSearchParams(window.location.search)
      const brandCode = urlParams.get('brand')
      if (brandCode && brandCode !== selectedBrandCode) {
        dispatch(setBrandCode(brandCode))
      }
      const currency = urlParams.get('cur')
      if (currency) {
        dispatch(setCurrency(currency))
      }
      setShouldRender(true)
    }, 10)
  }, [window.location.search])

  // Handlers
  const onNewShipCode = shipCode => {
    dispatch(setShipCode(shipCode))
    dispatch(setDate(null))

    // NOTE(ebarrett): We need to fetch the calendar data for the new ship
    //  to adjust which dates are highlighted in the date select
    if (shipCode) {
      dispatch(fetchCalendar(selectedBrandCode, shipCode))
    }
  }

  const setSelectedDate = date => {
    const formattedDate = date ? getFormattedDate(date) : null
    dispatch(setDate(formattedDate))
  }

  const isDayHighlighted = date => {
    const dayKey = date.format('YYYY-MM-DD')
    return !!validDates[dayKey]
  }

  // Layout
  return (
    <div className="app__base app" data-testid="app__base">
      <Switch>
        <Route exact path="/cruiseplanner">
          {shouldRender && (
            <BrandForm
              selectedShipCode={selectedShipCode}
              setSelectedShipCode={onNewShipCode}
              isDayHighlighted={isDayHighlighted}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              isValidSearch={isValidSearch}
              validDates={validDates}
              {...content}
            />
          )}
        </Route>
      </Switch>
    </div>
  )
}

export default App
